import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Button, Card, EmptyState, FormRow, Modal, ModalFooter, Notice, PageHeader, SelectField, Stat, StatGrid, Table, TextField, useToast } from "@plataforma/design-system";
import type { Expense, ExpenseInput, ExtraRevenue, ExtraRevenueInput, FinanceSummary } from "@plataforma/shared";
import { useAppConfig } from "../../app/config";
import { api, errorMessage } from "../../lib/api";
import { addDays, monthStart, todayIso } from "../../lib/format";
import "./finance.css";

const presets = { "months-6": "Últimos 6 meses", "months-12": "Últimos 12 meses", "30": "Últimos 30 dias", "90": "Últimos 90 dias", custom: "Personalizado" } as const;
type Preset = keyof typeof presets;

function rangeOf(preset: Preset, custom: { from: string; to: string }) {
  const to = todayIso();
  if (preset === "custom") return custom;
  if (preset.startsWith("months-")) return { from: monthStart(Number(preset.split("-")[1]) - 1), to };
  return { from: addDays(to, 1 - Number(preset)), to };
}

export function FinancePage() {
  const { config, format, has } = useAppConfig();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [preset, setPreset] = useState<Preset>("months-6");
  const [custom, setCustom] = useState({ from: monthStart(), to: todayIso() });
  const [adding, setAdding] = useState<"expense" | "revenue" | null>(null);
  const range = rangeOf(preset, custom);
  const valid = range.from <= range.to;
  const summary = useQuery({ queryKey: ["finance", range], enabled: valid, queryFn: async () => (await api.get<{ summary: FinanceSummary }>(`/finance/summary?from=${range.from}&to=${range.to}`)).summary, placeholderData: (previous) => previous });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["finance"] });

  // Remover devolve o registro; "Desfazer" simplesmente o recria.
  const remove = useMutation({
    mutationFn: async (target: { kind: "expenses" | "extra-revenues"; id: string }) => ({ kind: target.kind, removed: (await api.del<{ removed: Expense | ExtraRevenue }>(`/finance/${target.kind}/${target.id}`)).removed }),
    onSuccess: ({ kind, removed }) => {
      void refresh();
      const { id: _id, ...input } = removed;
      toast(kind === "expenses" ? "Despesa removida." : "Receita removida.", { label: "Desfazer", run: () => void api.post(`/finance/${kind}`, input).then(refresh) });
    },
    onError: (failure) => toast(errorMessage(failure)),
  });

  const data = summary.data;
  const peak = Math.max(1, ...(data?.months ?? []).flatMap((month) => [month.invoiceRevenue + month.extraRevenue, month.expenses]));

  return (
    <>
      <PageHeader title="Financeiro" subtitle="Receita, despesas e resultado estimado de cada mês." />
      <Card padded>
        <FormRow>
          <SelectField label="Período" value={preset} onChange={(event) => setPreset(event.target.value as Preset)}>
            {Object.entries(presets).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </SelectField>
          {preset === "custom" ? (
            <FormRow>
              <TextField label="De" type="date" value={custom.from} max={custom.to} onChange={(event) => setCustom({ ...custom, from: event.target.value })} />
              <TextField label="Até" type="date" value={custom.to} min={custom.from} onChange={(event) => setCustom({ ...custom, to: event.target.value })} />
            </FormRow>
          ) : <p className="ds-muted ds-small" style={{ alignSelf: "center" }}>{format.date(range.from)} a {format.date(range.to)}</p>}
        </FormRow>
        {!valid ? <Notice tone="danger">A data inicial deve ser anterior ou igual à final.</Notice> : null}
      </Card>
      {data ? (
        <>
          <div style={{ height: 16 }} />
          <StatGrid>
            <Stat label="Receita total" value={format.money(data.revenue)} hint={has("invoices") ? `Notas ${format.money(data.invoiceRevenue)} + extras ${format.money(data.extraRevenue)}` : "Receitas registradas"} />
            <Stat label="Despesas" value={format.money(data.expenses)} hint="Pontuais e recorrentes" />
            <Stat label="Resultado estimado" value={format.money(data.result)} hint="Receita menos despesas" />
            {has("invoices") ? <Stat label="Notas pendentes" value={data.pendingInvoices.count} hint={data.pendingInvoices.count ? `${format.money(data.pendingInvoices.value)} em valores combinados` : "nenhuma no período"} /> : null}
          </StatGrid>
          <Card title="Receita e despesas por mês">
            <div className="finance-chart" role="img" aria-label="Gráfico de receita e despesas por mês">
              {data.months.map((month) => (
                <div key={month.month} className="finance-chart__column">
                  <div className="finance-chart__bars">
                    <span className="finance-chart__bar finance-chart__bar--revenue" style={{ height: `${((month.invoiceRevenue + month.extraRevenue) / peak) * 100}%` }} title={`Receita: ${format.money(month.invoiceRevenue + month.extraRevenue)}`} />
                    <span className="finance-chart__bar finance-chart__bar--expense" style={{ height: `${(month.expenses / peak) * 100}%` }} title={`Despesas: ${format.money(month.expenses)}`} />
                  </div>
                  <span className="ds-muted ds-small">{format.month(month.month)}</span>
                </div>
              ))}
            </div>
            <Table
              items={data.months}
              rowKey={(month) => month.month}
              columns={[
                { header: "Mês", cell: (month) => <strong>{format.month(month.month)}</strong> },
                ...(has("invoices") ? [{ header: "Notas emitidas", numeric: true, cell: (month: FinanceSummary["months"][number]) => format.money(month.invoiceRevenue) }] : []),
                { header: "Receitas extras", numeric: true, cell: (month) => format.money(month.extraRevenue) },
                { header: "Despesas", numeric: true, cell: (month) => format.money(month.expenses) },
                { header: "Resultado estimado", numeric: true, cell: (month) => format.money(month.result) },
              ]}
            />
            <p className="ds-muted ds-small" style={{ padding: "10px 16px" }}>Estimativa operacional: nota emitida não confirma recebimento, e o resultado não inclui impostos nem despesas não lançadas.</p>
          </Card>
          <div style={{ height: 16 }} />
          <Card title="Receitas extras" actions={<Button size="small" variant="primary" onClick={() => setAdding("revenue")}>＋ Registrar receita</Button>}>
            <Table<ExtraRevenue> items={data.extraRevenues} rowKey={(item) => item.id} empty={<EmptyState>Nenhuma receita extra no período.</EmptyState>} columns={[
              { header: "Origem / descrição", cell: (item) => <><strong>{item.source}</strong><div className="ds-muted ds-small">{item.description}</div></> },
              { header: "Data", cell: (item) => format.date(item.date) },
              { header: "Valor", numeric: true, cell: (item) => format.money(item.amount) },
              { header: "", cell: (item) => <Button variant="quiet" tone="danger" onClick={() => remove.mutate({ kind: "extra-revenues", id: item.id })}>Remover</Button> },
            ]} />
          </Card>
          <div style={{ height: 16 }} />
          <Card title="Despesas" actions={<Button size="small" variant="primary" onClick={() => setAdding("expense")}>＋ Registrar despesa</Button>}>
            <Table<Expense> items={data.expenseEntries} rowKey={(item) => item.id} empty={<EmptyState>Nenhuma despesa no período.</EmptyState>} columns={[
              { header: "Categoria / descrição", cell: (item) => <><strong>{item.category}</strong><div className="ds-muted ds-small">{item.description}</div></> },
              { header: "Início", cell: (item) => format.date(item.date) },
              { header: "Frequência", cell: (item) => (item.recurrence === "monthly" ? "Mensal" : "Pontual") },
              { header: "Valor por ocorrência", numeric: true, cell: (item) => format.money(item.amount) },
              { header: "", cell: (item) => <Button variant="quiet" tone="danger" onClick={() => remove.mutate({ kind: "expenses", id: item.id })}>Remover</Button> },
            ]} />
          </Card>
        </>
      ) : valid ? <EmptyState>{summary.isError ? errorMessage(summary.error) : "Carregando…"}</EmptyState> : null}
      {adding ? <EntryForm kind={adding} options={adding === "expense" ? config.settings.expenseCategories : config.settings.revenueSources} onClose={() => setAdding(null)} onSaved={refresh} /> : null}
    </>
  );
}

function EntryForm({ kind, options, onClose, onSaved }: { kind: "expense" | "revenue"; options: string[]; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [values, setValues] = useState({ group: options[0] ?? "", description: "", amount: "", date: todayIso(), recurrence: "once" as "once" | "monthly" });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const set = (field: keyof typeof values) => (event: { target: { value: string } }) => setValues((current) => ({ ...current, [field]: event.target.value }));
  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      const common = { description: values.description, amount: Number(values.amount), date: values.date };
      if (kind === "expense") await api.post("/finance/expenses", { ...common, category: values.group, recurrence: values.recurrence } satisfies ExpenseInput);
      else await api.post("/finance/extra-revenues", { ...common, source: values.group } satisfies ExtraRevenueInput);
      toast(kind === "expense" ? "Despesa registrada." : "Receita registrada.");
      onSaved();
      onClose();
    } catch (failure) {
      setError(errorMessage(failure));
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal title={kind === "expense" ? "Registrar despesa" : "Registrar receita extra"} onClose={onClose} dirty={values.description.length > 0 || values.amount.length > 0}>
      <form onSubmit={submit}>
        {kind === "revenue" ? <div style={{ marginBottom: 14 }}><Notice>Lance aqui apenas valores que não estejam numa nota fiscal deste sistema, para evitar contagem dupla.</Notice></div> : null}
        <SelectField label={kind === "expense" ? "Categoria" : "Origem"} value={values.group} onChange={set("group")}>{options.map((option) => <option key={option}>{option}</option>)}</SelectField>
        <TextField label="Descrição" required maxLength={100} value={values.description} onChange={set("description")} />
        <FormRow>
          <TextField label="Valor" type="number" min="0.01" step="0.01" required inputMode="decimal" value={values.amount} onChange={set("amount")} />
          <TextField label={kind === "expense" ? "Data de início ou pagamento" : "Data"} type="date" required value={values.date} onChange={set("date")} />
        </FormRow>
        {kind === "expense" ? (
          <SelectField label="Frequência" value={values.recurrence} onChange={set("recurrence")} hint="Mensal repete o mesmo valor a cada mês a partir da data indicada.">
            <option value="once">Pontual</option>
            <option value="monthly">Mensal</option>
          </SelectField>
        ) : null}
        {error ? <p className="ds-field__error" role="alert">{error}</p> : null}
        <ModalFooter>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="primary" disabled={saving}>Salvar</Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
